import {
  Component, OnInit, Inject, OnDestroy
} from '@angular/core';
import {
  Subject,
  takeUntil,
  Observable,
  first,
  combineLatest,
} from 'rxjs';
import {
  buildIntakeEndpoint,
  WizardService,
  FLOWS_INTAKE_SLUGS,
  MAIN_INTAKE_FLOW_SLUG
} from '@sondermind/flows-host';
import { GtmDataLayerService, GtmCustomEvents } from '@sondermind/google-tag-manager';

import { FEATURE_FLAG_SERVICE, FeatureFlagKey, IFeatureFlagService } from '@sondermind/data-access/feature-flags';

import { BrowserNavButtonsService } from '@sondermind/utilities/browser-nav-buttons';
import { FlowInfo } from '@sondermind/utilities/models-flows';
import { UserSystemInfoService } from '@sondermind/utilities/services';

@Component({
  selector: 'sonder-flow-boot',
  templateUrl: './app-boot.component.html',
  styleUrls: ['./app-boot.component.scss'],
  standalone: false
})
export class AppBootComponent implements OnInit, OnDestroy {
  flowInfo: FlowInfo = new FlowInfo(MAIN_INTAKE_FLOW_SLUG);

  private flowsSupportingNavButtons: string[] = [
    MAIN_INTAKE_FLOW_SLUG,
    'matching-flow-skip-to-location',
    'matching-flow-location-ui-upgrade',
    'directory-matching-flow',
    'matching-flow-auth0'
  ];

  shouldUseFlowsIntake: boolean = true;
  private destroyedSubj: Subject<void> = new Subject<void>();
  destroyed$: Observable<void> = this.destroyedSubj.asObservable();

  constructor(
    public wizardSvc: WizardService,
    private gtmDataLayerService: GtmDataLayerService,
    private userSystemInfoService: UserSystemInfoService,
    private browserNavButtonsService: BrowserNavButtonsService,
    @Inject(FEATURE_FLAG_SERVICE)
    private featureFlagService: IFeatureFlagService
  ) {}

  ngOnInit(): void {
    this.featureFlagService.initialize$().pipe(
      first((initialized) => initialized),
    ).subscribe(() => {
      this.setFlowInfo();
    });
  }

  ngOnDestroy(): void {
    // even-though browserNavButtonsService init() is called conditionally,
    //  it is safe to unconditionally call destroy()
    this.browserNavButtonsService.destroy();
    this.wizardSvc.detachBrowserButtonService();

    this.destroyedSubj.next();
    this.destroyedSubj.complete();
  }

  private setupMatchFlow(): void {
    this.wizardSvc.response$.pipe(
      takeUntil(this.destroyed$)
    ).subscribe(
      (resp) => {
        this.shouldUseFlowsIntake = FLOWS_INTAKE_SLUGS.includes(resp?.slug);
      }
    );

    this.wizardSvc.toggleRunning(true);
    this.initializeMatchFlow();

    if (this.flowsSupportingNavButtons.includes(this.flowInfo.slug)) {
      this.browserNavButtonsService.init();
      this.wizardSvc.attachBrowserButtonService(this.browserNavButtonsService);
    }
  }

  private initializeMatchFlow(): void {
    this.setMatchFlowDefaultOnDataLayer();
    combineLatest({
      experimentKey: this.featureFlagService.anonymousId$(),
      wizard: this.wizardSvc.initialize(
        this.flowInfo.slug,
        { endpoint_builder: buildIntakeEndpoint },
        this.flowInfo.hash,
        this.flowInfo.shouldLoadStrict
      )
    }).subscribe(({ experimentKey }) => {
      if (this.wizardSvc.started) {
        this.wizardSvc.start([], true).subscribe();
      }

      const matchFlowInitData = [
        { human: 'Feature Flag key', key: 'experimentationKey', value: experimentKey },
        { human: 'Matchflow Domain', key: 'domain', value: window.location.hostname },
        { human: 'Document Referrer', key: 'referrer', value: document.referrer },
        { human: 'Path Name', key: 'pathname', value: window.location.pathname },
      ];
      const gaId = this.getGoogleAnalyticsClientId();
      if (gaId) {
        matchFlowInitData.push({ human: 'GA Client ID', key: 'ga_id', value: gaId });
      }

      this.wizardSvc.start(matchFlowInitData, true).subscribe(() => {
        this.wizardSvc.toggleRunning(true);
      });
    });
  }

  private setFlowInfo(): void {
    const urlParams = new URLSearchParams(window.location.search);

    if (urlParams.has('slug')) {
      this.flowInfo.slug = urlParams.get('slug');
      this.flowInfo.hash = urlParams.get('hash');
      this.flowInfo.shouldLoadStrict = urlParams.get('strict') === 'yes';
      this.setupMatchFlow();
    } else {
      this.featureFlagService.evaluate$(
        FeatureFlagKey.INTAKE_MATCH_FLOW_SLUG,
        null
      ).pipe(
        first(),
      ).subscribe({
        next: (flag) => {
          this.flowInfo.slug = flag?.key ?? MAIN_INTAKE_FLOW_SLUG;
          this.flowInfo.hash = null;
          this.setupMatchFlow();
        },
        error: () => {
          this.setupMatchFlow();
        }
      });
    }
  }

  /**
   * Method for setting the match flow default custom event with user system metadata.
   *
   * @private
   * @memberof AppBootComponent
   */
  private setMatchFlowDefaultOnDataLayer(): void {
    const browserInfo = this.userSystemInfoService.userSystemInfo.browser;
    const platformInfo = this.userSystemInfoService.userSystemInfo.platform;
    /* eslint-disable no-extra-boolean-cast */
    const browser = !!browserInfo ? `${browserInfo.name} ${browserInfo.version}` : '';
    const platform = !!platformInfo ? `${platformInfo.type} ${platformInfo.vendor}` : '';
    /* eslint-enable no-extra-boolean-cast */

    this.gtmDataLayerService.dataLayer = {
      event: GtmCustomEvents.MATCH_FLOW_DEFAULT,
      category: GtmCustomEvents.MATCH_FLOW_DEFAULT,
      action: browser,
      label: platform
    };
  }

  private getGoogleAnalyticsClientId(): string {
    // Regex to get the _ga cookie value from the document.cookie string
    let match = /(?:^|;)\s*_ga=([^;]*)/.exec(document.cookie);
    const raw = match ? decodeURIComponent(match[1]) : null;
    if (raw) {
      match = /(\d+\.\d+)$/.exec(raw);
    }
    return match ? match[1] : null;
  }
}
